import { useEffect, useState, useMemo } from 'react';
import { fetchQuery, graphql } from 'react-relay';
import type { MentionDescription } from '@atlaskit/mention';
import type { TeamMember } from '@atlassian/jira-issue-view-common-types/src/user-type.tsx';
import { IdentityTeamAri } from '@atlassian/ari/identity/team';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import getRelayEnvironment from '@atlassian/jira-relay-environment/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type {
	dataForRecommendationsInEditorQuery,
	dataForRecommendationsInEditorQuery$data,
} from '@atlassian/jira-relay/src/__generated__/dataForRecommendationsInEditorQuery.graphql';

const ACCESS_LEVEL = 'CONTAINER';
const USER_TYPE = 'TEAM';

type UserTeamMention = {
	id: string;
	name: string;
	avatarUrl?: string;
	mentionName?: string;
	nickname?: string;
	accessLevel?: string;
	userType?: string;
	context?: {
		members: TeamMember[];
		memberCount: number;
		includesYou: boolean;
		teamLink: string;
	};
};

const getTeamUrl = (teamId: undefined | string) => {
	if (teamId === null || teamId === '') {
		return '';
	}

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	return `${window.location.origin}/jira/people/team/${teamId}?ref=jira$&src=issue`;
};

const mentionsDataQuery = graphql`
	query dataForRecommendationsInEditorQuery(
		$cloudId: ID!
		$siteId: String!
		$query: String!
		$issueKey: String!
		$sessionId: String!
		$maxResults: Int!
		$organizationId: String!
	) {
		jira {
			searchUserTeamMention(
				cloudId: $cloudId
				query: $query
				issueKey: $issueKey
				sessionId: $sessionId
				maxResults: $maxResults
				organizationId: $organizationId
			) {
				edges @required(action: THROW) {
					node @required(action: THROW) {
						team {
							fullTeam(siteId: $siteId) {
								id
								displayName
								smallAvatarImageUrl
								members {
									nodes {
										member {
											id
											accountId
											name
											picture
										}
									}
								}
							}
							jiraIncludesYou
							jiraMemberCount
						}
						user {
							accountId
							name
							picture
							__typename
						}
					}
				}
			}
		}
	}
`;

const transformMentionableResponse = (
	response: dataForRecommendationsInEditorQuery$data,
): UserTeamMention[] => {
	const mentions = response.jira?.searchUserTeamMention?.edges.map((edge) => {
		const { user, team } = edge?.node || {};
		if (team) {
			return {
				id: team.fullTeam?.id || '',
				name: team.fullTeam?.displayName || '',
				accessLevel: ACCESS_LEVEL,
				userType: USER_TYPE,
				avatarUrl: team.fullTeam?.smallAvatarImageUrl || '',
				context: {
					members:
						team.fullTeam?.members?.nodes?.map((node) => ({
							id: node?.member?.accountId || '',
							name: node?.member?.name || '',
						})) || [],
					memberCount: team.jiraMemberCount || 0,
					includesYou: team.jiraIncludesYou || false,
					teamLink: getTeamUrl(IdentityTeamAri.parse(team.fullTeam?.id || '').teamId),
				},
			};
		}
		return {
			name: user?.name || '',
			mentionName: user?.name,
			nickname: user?.name,
			id: user?.accountId || '',
			avatarUrl: user?.picture || '',
		};
	});

	return mentions || [];
};

export const useDataForRecommendationsInEditor = (): MentionDescription[] => {
	const [mentionsUserData, setMentionsUserData] = useState<MentionDescription[]>([]);
	const issueKey = useIssueKey();
	const cloudId = useCloudId();

	const variables = useMemo(
		() => ({
			query: '',
			issueKey,
			organizationId: '',
			maxResults: 20,
			sessionId: '',
			cloudId,
			siteId: cloudId,
		}),
		[issueKey, cloudId],
	);

	const environment = useMemo(() => getRelayEnvironment(), []);

	useEffect(() => {
		const fetchMentionsUserData = async () => {
			try {
				const response = await fetchQuery<dataForRecommendationsInEditorQuery>(
					environment,
					mentionsDataQuery,
					variables,
				).toPromise();
				const transformedMentionableResponse = response
					? transformMentionableResponse(response)
					: [];
				setMentionsUserData(transformedMentionableResponse);
			} catch (error) {
				throw new Error(`Error fetching mentions user data: ${error}`);
			}
		};
		fetchMentionsUserData();
	}, [environment, variables]);

	return mentionsUserData;
};
